/** Channel Lineup analytics — the pure half. */

import type { ProductImpressionItem } from "../../services/analytics";

/** AN1's five page classifications — "an Upper Funnel, Video, Prospect page". */
export const CHANNEL_LINEUP_PAGE_LOAD = {
  applicationName: "UF",
  pageFunction: "Other",
  pageOwnership: "Video",
  flowCode: "Prospect",
  lineOfBusiness: "Video"
};

/** AN3 — the Shop click's list, which reports under TWO different strings.
 *  The id is not a slug of the name and the name is not a prettying of the id;
 *  both are verbatim from `PackageCmprGrid.tsx:118-134`. */
export const SHOP_ITEM_LIST_ID = "ChannelLineup-PackagesHeader";
export const SHOP_ITEM_LIST_NAME = "Channel Lineup Packages";

/**
 * AN3's link name — "the link is also tracked in lower case as `{button label}_{package name}`, for
 * example `shop_entertainment`". An underscore, and the whole string folded.
 */
export function shopLinkName(ctaLabel?: string, packageName?: string): string {
  return `${ctaLabel ?? ""}_${packageName ?? ""}`.toLowerCase();
}

/**
 * AN4's "radio-button product click" — the three fixed strings `fusion.atoms.radio-button` reports
 * for itself, which the live page sends today (`PackageCmprGrid.tsx:927-936`) and which a
 * DDS-native radio group has to keep sending.
 */
export const SORT_RADIO_PRODUCT = {
  tag: "Radiobutton",
  productEvent: "Checked",
  productEventName: "Radiobutton"
};

/** AN5 — which search this is. There is no default in the service and none
 *  worth guessing; this is the one the lineup files its searches under. */
export const CHANNEL_LINEUP_SEARCH_CATEGORY = "channelLineup";

/** AN5 — what a suggestion with no wording reports as, lower-cased like every
 *  other term. Production's own fallback (`ChannelSearch.tsx:336`). */
export const NO_SUGGESTION_TERM = "no suggestion";

/** AN8 — the Reset control's reported name, verbatim from the live page's own
 *  `aria-label` (`ChannelSearch.tsx:498`). Lower case is the value, not a
 *  transform: the rows already collected are filed under this spelling. */
export const RESET_SEARCH_LINK_NAME = "reset search";

/**
 * AN5's last clause — "clicking into the search field sends a link click named `Search Channels`",
 * verbatim from the live field's own `aria-label` (`ChannelSearch.tsx:560`).
 */
export const SEARCH_FIELD_LINK_NAME = "Search Channels";

/**
 * The page's own address, which AN4's and AN8's in-page reports carry as their destination —
 * production fills all three from `useLocationHref()` (`ChannelSearch.tsx:467`, `:507`, the legacy
 * tab tray's third argument).
 */
export function pageHref(): string {
  if (typeof window === "undefined") {
    return "";
  }
  const href = window.location && window.location.href;
  return typeof href === "string" ? href : "";
}

/** Anything carrying an optional analytics id — a `LineupColumn` or a
 *  `LineupPack`. Typed structurally so this file imports neither. */
export interface AnalyticsIdentified {
  analyticsId?: string;
}

/**
 * AN2 — "each package is listed with its position, starting at 1. Packages without an analytics ID
 * are skipped." it filters and then numbers, in that order, which is what production does
 * (`ChnlListingPage.tsx:58-68`) and what makes three packages with an id-less middle one report
 * positions 1 and 2 rather than 1 and 3. Numbering first and filtering after leaves a hole in the
 * sequence, and nothing downstream notices — the impression simply reports a list the reader never
 * saw.
 */
export function productImpressionItems(
  packages?: readonly AnalyticsIdentified[]
): ProductImpressionItem[] {
  const items: ProductImpressionItem[] = [];

  for (const entry of packages ?? []) {
    const id = entry?.analyticsId;
    /* Blank is absent: a package-config lookup that answered with an empty string would otherwise
       report a product nothing can be joined to. */
    if (typeof id !== "string" || id.trim() === "") {
      continue;
    }
    items.push({
      id,
      position: items.length + 1
    });
  }

  return items;
}

/**
 * AN4's first report — "changing the sort sends a link click named `Display_{option}`". Not
 * lower-cased, where `sortRadioLinkName` beside it is.
 */
export function sortDisplayLinkName(optionLabel: string): string {
  return `Display_${optionLabel ?? ""}`;
}

/** AN4's second report — the radio's own name, lower-cased. See above for why
 *  this one folds case and `Display_` does not. */
export function sortRadioLinkName(optionLabel: string): string {
  return `sortby_${optionLabel ?? ""}`.toLowerCase();
}

/** AN8 — the view switch, "named after the view switched to". That is the whole fix. */
export function viewSwitchLinkName(optionLabel: string): string {
  return `Show Channel By ${(optionLabel ?? "").toLowerCase()}`;
}

/** AN5 — a chosen suggestion's own search term, lower-cased. A row with no
 *  wording reports `NO_SUGGESTION_TERM` rather than an empty search. */
export function suggestionSearchTerm(label?: string): string {
  const wording = (label ?? "").trim();
  return wording === "" ? NO_SUGGESTION_TERM : wording.toLowerCase();
}

/**
 * AN5's select-all term — "the prefix and the typed text joined with no space, in lower case (for
 * example `all channels includinghbo`), although the row on screen shows a space".
 */
export function selectAllSearchTerm(prefix?: string, typedText?: string): string {
  return `${prefix ?? ""}${typedText ?? ""}`.toLowerCase();
}

/** AN5's select-all LINK name — the same two parts joined with a hyphen
 *  (`all channels including-hbo`). See `selectAllSearchTerm` for why the two
 *  joins differ. */
export function selectAllLinkName(prefix?: string, typedText?: string): string {
  return `${prefix ?? ""}-${typedText ?? ""}`.toLowerCase();
}

/**
 * AN5 — a chosen suggestion's own link name, "search - {channel name} channel {number}".
 * Production's `buildRowAnnouncement` (`DropdownList.tsx:36-39`). The name only, never AX11's `,
 * {i} of {n}` suffix.
 */
export function suggestionLinkName(label?: string, secondary?: string): string {
  const name = (label ?? "").trim().toLowerCase();
  const number = (secondary ?? "").trim();
  return number === "" ? `search - ${name}` : `search - ${name} channel ${number}`;
}

/**
 * AN5's other half — a chip removal's link name, "remove {channel name} channel {number}".
 * Production's `buildRemoveLabel` (`FilterChips.tsx:24-27`), under the same two rules as
 * `suggestionLinkName` and without its twin suffix.
 */
export function chipRemoveLinkName(label?: string, secondary?: string): string {
  const name = (label ?? "").trim().toLowerCase();
  const number = (secondary ?? "").trim();
  return number === "" ? `remove ${name}` : `remove ${name} channel ${number}`;
}
